import React, { useEffect, useState } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import toast from 'react-hot-toast';
import { EyeIcon, EyeSlashIcon } from '@heroicons/react/24/outline';
import { API_BASE } from '../lib/config';

const LoginPage = () => {
  const [formData, setFormData] = useState({
    email: '',
    password: ''
  });
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);

  const { login, getDashboardUrl } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  const from = location.state?.from?.pathname || '/';

  const handleSocialLogin = async (provider) => {
    try {
      setLoading(true);
      const frontUrl = window.location.origin;
      const res = await fetch(`${API_BASE}/auth/${provider}/url?front_url=${encodeURIComponent(frontUrl)}`);
      const data = await res.json();
      if (data.url) {
        window.location.href = data.url;
      } else {
        toast.error('ログインURLの取得に失敗しました');
      }
    } catch (e) {
      console.error(e);
      toast.error('エラーが発生しました');
    } finally {
      setLoading(false);
    }
  };

  // サインアップ直後の pending 資格情報を事前入力し、?auto=1 がある場合は自動ログイン
  useEffect(() => {
    try {
      const pendingEmail = localStorage.getItem('pending_email');
      const pendingPassword = localStorage.getItem('pending_password');
      if (pendingEmail && pendingPassword) {
        setFormData({ email: pendingEmail, password: pendingPassword });
        const params = new URLSearchParams(location.search);
        const shouldAuto = params.get('auto') === '1';
        if (shouldAuto) {
          // 少し待ってから自動送信（状態反映のため）
          setTimeout(() => {
            void handleSubmit(new Event('submit'));
          }, 200);
        }
      }
      // URL の ?type=employer|job_seeker を pending_user_type に反映（ローカル開発のための補助）
      const params = new URLSearchParams(location.search);
      const t = params.get('type');
      if (t === 'employer' || t === 'job_seeker') {
        try { localStorage.setItem('pending_user_type', t); } catch { }
      }
    } catch (e) {
      // noop
    }
    // location.search 依存でOK

  }, [location.search]);

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      console.log('=== LOGIN PROCESS START ===');
      console.log('Form data:', formData);

      // ローカル開発時に事業者として入りたい場合は、?type=employer を付けてこの画面に来る
      // または、以下の pending_user_type を明示的に employer にしておく
      try {
        const params = new URLSearchParams(location.search);
        const t = params.get('type');
        if (t === 'employer' || t === 'job_seeker') {
          localStorage.setItem('pending_user_type', t);
        }
      } catch { }

      const result = await login(formData.email, formData.password);
      console.log('Login result:', result);

      if (result.success) {
        console.log('Login successful, user data:', result.user);
        console.log('User type from login:', result.user.user_type);

        toast.success('ログインしました！');
        // pending をクリア
        try {
          localStorage.removeItem('pending_email');
          localStorage.removeItem('pending_password');
          localStorage.removeItem('pending_user_type');
        } catch (e) { }

        // ユーザータイプに基づいて適切なダッシュボードにリダイレクト
        const dashboardUrl = getDashboardUrl(result.user.user_type);
        console.log('Dashboard URL calculated:', dashboardUrl);
        console.log('Navigating to:', dashboardUrl);

        // 認証状態が確実に設定されるまで少し待機
        setTimeout(() => {
          console.log('Executing navigation to:', dashboardUrl);
          navigate(dashboardUrl, { replace: true });
        }, 100);
      } else {
        console.error('Login failed:', result.error);
        toast.error(result.error);
      }
    } catch (error) {
      console.error('Login exception:', error);
      toast.error(error.response?.data?.detail || 'ログインに失敗しました');
    } finally {
      setLoading(false);
      console.log('=== LOGIN PROCESS END ===');
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col justify-center py-12 sm:px-6 lg:px-8">
      <div className="sm:mx-auto sm:w-full sm:max-w-md">
        <div className="text-center">
          <h2 className="text-3xl font-bold text-gray-900">byters</h2>
          <p className="mt-2 text-sm text-gray-600">即日バイトマッチング</p>
        </div>
        <h2 className="mt-6 text-center text-3xl font-extrabold text-gray-900">
          アカウントにログイン
        </h2>
        <p className="mt-2 text-center text-sm text-gray-600">
          アカウントをお持ちでない方は{' '}
          <Link to="/register/type" className="font-medium text-blue-600 hover:text-blue-500">
            新規登録
          </Link>
        </p>
      </div>

      <div className="mt-8 sm:mx-auto sm:w-full sm:max-w-md">
        <div className="bg-white py-8 px-4 shadow sm:rounded-lg sm:px-10">

          {/* Social Login Buttons */}
          <div className="mb-6 space-y-3">
            <button
              type="button"
              onClick={() => handleSocialLogin('google')}
              className="w-full flex items-center justify-center px-4 py-2 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
            >
              <svg className="h-5 w-5 mr-2" viewBox="0 0 24 24">
                <path
                  d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
                  fill="#4285F4"
                />
                <path
                  d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
                  fill="#34A853"
                />
                <path
                  d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
                  fill="#FBBC05"
                />
                <path
                  d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
                  fill="#EA4335"
                />
              </svg>
              Googleでログイン
            </button>
            <button
              type="button"
              onClick={() => handleSocialLogin('line')}
              className="w-full flex items-center justify-center px-4 py-2 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-[#00B900] hover:bg-[#009900] focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#00B900]"
            >
              <svg className="h-5 w-5 mr-2" viewBox="0 0 24 24" fill="currentColor">
                <path d="M20.2 6.8c.2 1.3.2 2.6.2 3.9 0 5.5-4.9 10.3-10.9 9.3C4 19.1.5 14.8.5 9.8c0-5 4.5-9 9.9-9 4.3-.1 8.5 2.6 9.8 6z" />
                <path d="M12 1C5.9 1 1 5.4 1 10.8c0 5 4.2 9.1 9.8 9.6 1.1.1 1.3-.8 1.2-1.4v-2.2c0-.5 0-.5 1-1 4.5-.4 7.6-3.8 7.6-7.8C20.6 4.3 16.5 1 12 1zm5.6 11.6h-3.6c-.3 0-.5-.2-.5-.5v-4c0-.3.2-.5.5-.5.3 0 .5.2.5.5v3.5h3.1c.3 0 .5.2.5.5 0 .3-.2.5-.5.5zm-5.4 0c0 .3-.2.5-.5.5h-.1c-.1 0-.3-.1-.4-.2l-2.4-3.3v3c0 .3-.2.5-.5.5h-.1c-.3 0-.5-.2-.5-.5v-4c0-.3.2-.5.5-.5.1 0 .3.1.4.2l2.4 3.3v-3c0-.3.2-.5.5-.5s.5.2.5.5v4zm-5.4 0H3.7c-.3 0-.5-.2-.5-.5v-4c0-.3.2-.5.5-.5s.5.2.5.5v4c0 .3-.2.5.5.5z" stroke="white" strokeWidth="1.5" />
              </svg>
              LINEでログイン
            </button>
          </div>

          <div className="relative mb-6">
            <div className="absolute inset-0 flex items-center">
              <div className="w-full border-t border-gray-300" />
            </div>
            <div className="relative flex justify-center text-sm">
              <span className="px-2 bg-white text-gray-500">
                またはメールアドレスでログイン
              </span>
            </div>
          </div>

          <form className="space-y-6" onSubmit={handleSubmit}>
            <div>
              <label htmlFor="email" className="form-label">
                メールアドレス
              </label>
              <input
                id="email"
                name="email"
                type="email"
                autoComplete="email"
                required
                className="form-input"
                value={formData.email}
                onChange={handleChange}
                placeholder="your@example.com"
              />
            </div>

            <div>
              <label htmlFor="password" className="form-label">
                パスワード
              </label>
              <div className="relative">
                <input
                  id="password"
                  name="password"
                  type={showPassword ? 'text' : 'password'}
                  autoComplete="current-password"
                  required
                  className="form-input pr-10"
                  value={formData.password}
                  onChange={handleChange}
                  placeholder="パスワードを入力"
                />
                <button
                  type="button"
                  className="absolute inset-y-0 right-0 pr-3 flex items-center"
                  onClick={() => setShowPassword(!showPassword)}
                >
                  {showPassword ? (
                    <EyeSlashIcon className="h-5 w-5 text-gray-400" />
                  ) : (
                    <EyeIcon className="h-5 w-5 text-gray-400" />
                  )}
                </button>
              </div>
            </div>

            <div>
              <button
                type="submit"
                disabled={loading}
                className="form-button disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {loading ? (
                  <div className="flex items-center justify-center">
                    <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white mr-2"></div>
                    ログイン中...
                  </div>
                ) : (
                  'ログイン'
                )}
              </button>
            </div>
          </form>

          {/* Guest Search Link */}
          <div className="mt-6">
            <div className="relative">
              <div className="absolute inset-0 flex items-center">
                <div className="w-full border-t border-gray-300" />
              </div>
              <div className="relative flex justify-center text-sm">
                <span className="px-2 bg-white text-gray-500">
                  登録前に探してみる
                </span>
              </div>
            </div>
            <div className="mt-4 text-center">
              <Link to="/mobile-search" className="text-sm font-medium text-blue-600 hover:text-blue-500 flex items-center justify-center group">
                <span className="mr-1">まずは求人を見てみる (ゲスト検索)</span>
                <svg className="w-4 h-4 transform group-hover:translate-x-1 transition-transform" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M17 8l4 4m0 0l-4 4m4-4H3" /></svg>
              </Link>
            </div>
          </div>

        </div>
      </div>
    </div>
  );
};

export default LoginPage;